// src/pages/ServiceDetail.jsx
import { useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { SITE, SERVICES, getServiceBySlug, serviceUrl } from "../data/site";
import { setPageSeo } from "../utils/seo";

export default function ServiceDetail() {
  const { slug } = useParams();

  const service = getServiceBySlug(slug);

  const PAGE_URL = service ? serviceUrl(service.slug) : `${SITE.domain}/services`;

  const SEO_TITLE = service
    ? service.seoTitle || `${service.title} Singapore | ${SITE.brand}`
    : `Service Not Found | ${SITE.brand}`;

  const SEO_DESC = service
    ? service.seoDesc ||
      `${service.desc} Site measurement, fabrication & installation in Singapore. Call ${SITE.phone}.`
    : `The requested service page was not found. View all services by ${SITE.brand} in Singapore.`;

  const schemas = useMemo(() => {
    const breadcrumbBase = [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE.domain },
      {
        "@type": "ListItem",
        position: 2,
        name: "Services",
        item: `${SITE.domain}/services`,
      },
    ];

    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: service
        ? [
            ...breadcrumbBase,
            {
              "@type": "ListItem",
              position: 3,
              name: service.title,
              item: PAGE_URL,
            },
          ]
        : breadcrumbBase,
    };

    const webPage = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: service ? service.title : "Service Not Found",
      url: PAGE_URL,
      description: SEO_DESC,
      isPartOf: { "@type": "WebSite", name: SITE.brand, url: SITE.domain },
    };

    const localBusiness = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: SITE.brand,
      url: SITE.domain,
      telephone: SITE.phone,
      email: SITE.emailPrimary,
      address: {
        "@type": "PostalAddress",
        streetAddress: SITE.addressLine,
        addressLocality: "Singapore",
        postalCode: "757700",
        addressCountry: "SG",
      },
      areaServed: "Singapore",
      geo: {
        "@type": "GeoCoordinates",
        latitude: SITE.geo.lat,
        longitude: SITE.geo.lng,
      },
    };

    const serviceSchema = service
      ? {
          "@context": "https://schema.org",
          "@type": "Service",
          name: `${service.title} in Singapore`,
          description: service.intro || service.desc,
          areaServed: "Singapore",
          url: PAGE_URL,
          provider: localBusiness,
        }
      : null;

    const faqSchema =
      service?.faqs?.length
        ? {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: service.faqs.map((x) => ({
              "@type": "Question",
              name: x.q,
              acceptedAnswer: { "@type": "Answer", text: x.a },
            })),
          }
        : null;

    return [breadcrumb, webPage, localBusiness, serviceSchema, faqSchema].filter(
      Boolean
    );
  }, [PAGE_URL, SEO_DESC, service]);

  useEffect(() => {
    setPageSeo({
      title: SEO_TITLE,
      description: SEO_DESC,
      canonical: PAGE_URL,
    });
  }, [PAGE_URL, SEO_DESC, SEO_TITLE]);

  if (!service) {
    return (
      <>
        {schemas.map((obj, idx) => (
          <script key={idx} type="application/ld+json">
            {JSON.stringify(obj)}
          </script>
        ))}

        <div className="max-w-4xl mx-auto px-4 py-16">
          <h1 className="text-3xl font-extrabold text-slate-900">
            Service not found
          </h1>
          <p className="mt-3 text-slate-600">
            The service page you requested doesn’t exist. Please check our
            services list.
          </p>
          <Link
            className="mt-6 inline-block text-blue-600 font-semibold"
            to="/services"
          >
            ← Back to Services
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      {schemas.map((obj, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}

      {/* HERO */}
      <section className="bg-gradient-to-b from-slate-50 to-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-12 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <nav className="text-sm text-slate-600">
              <Link to="/" className="hover:text-slate-900">
                Home
              </Link>
              <span className="mx-2">/</span>
              <Link to="/services" className="hover:text-slate-900">
                Services
              </Link>
              <span className="mx-2">/</span>
              <span className="text-slate-900 font-semibold">
                {service.title}
              </span>
            </nav>

            <h1 className="mt-4 text-3xl md:text-4xl font-extrabold text-slate-900">
              {service.title} in Singapore
            </h1>

            <p className="mt-3 text-slate-600 leading-relaxed">
              {service.intro || service.desc}
            </p>

            {service.keywords?.length ? (
              <p className="mt-3 text-sm text-slate-600">
                Popular searches:{" "}
                <span className="font-semibold text-slate-900">
                  {service.keywords.slice(0, 4).join(", ")}
                </span>
              </p>
            ) : null}

            <div className="mt-6 flex gap-3 flex-wrap">
              <Link
                to="/contact"
                className="px-5 py-3 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition"
              >
                Request a Quote
              </Link>

              <a
                href={`tel:${SITE.phone}`}
                className="px-5 py-3 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 transition"
              >
                Call Now
              </a>
            </div>

            <div className="mt-5 text-sm text-slate-600">
              <span className="font-semibold text-slate-900">Fast Quote:</span>{" "}
              Send location + size + photos on WhatsApp.
            </div>
          </div>

          <div className="rounded-3xl overflow-hidden border bg-white shadow-sm">
            <img
              src={service.heroImage || "/images/glass.jpg"}
              alt={`${service.title} service in Singapore`}
              className="h-64 sm:h-80 w-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="max-w-6xl mx-auto px-4 py-12 grid lg:grid-cols-3 gap-8">
        <article className="lg:col-span-2">
          <h2 className="text-2xl font-extrabold text-slate-900">
            {service.title} – Design, Fabrication & Installation
          </h2>

          <p className="mt-3 text-slate-600 leading-relaxed">
            {SITE.brand} provides end-to-end workmanship in Singapore: accurate
            site measurement, design proposal, fabrication, installation, clean
            finishing and after-sales support. We recommend materials based on
            weather exposure, mounting surface and usage requirement.
          </p>

          <div className="mt-8 grid gap-6">
            {service.sections?.map((sec, i) => (
              <section
                key={i}
                className="rounded-2xl border border-slate-200 bg-white p-6"
              >
                <h3 className="text-lg font-extrabold text-slate-900">
                  {sec.h}
                </h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{sec.p}</p>
              </section>
            ))}
          </div>

          {/* FAQ */}
          {service.faqs?.length ? (
            <section className="mt-10">
              <h2 className="text-2xl font-extrabold text-slate-900">
                Frequently Asked Questions – {service.title}
              </h2>

              <div className="mt-6 grid gap-3">
                {service.faqs.map((x) => (
                  <details
                    key={x.q}
                    className="group rounded-2xl border border-slate-200 bg-white p-5"
                  >
                    <summary className="cursor-pointer list-none font-bold text-slate-900 flex items-center justify-between">
                      <span>{x.q}</span>
                      <span className="text-slate-400 group-open:rotate-180 transition">
                        ▾
                      </span>
                    </summary>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                      {x.a}
                    </p>
                  </details>
                ))}
              </div>
            </section>
          ) : null}
        </article>

        {/* SIDEBAR */}
        <aside className="lg:col-span-1">
          <div className="sticky top-24 space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <h3 className="text-lg font-extrabold text-slate-900">
                Get a Quote
              </h3>
              <p className="mt-2 text-sm text-slate-600">
                Share your location, photos and approximate size. We’ll recommend
                a suitable solution.
              </p>

              <a
                href={`tel:${SITE.phone}`}
                className="mt-4 inline-block w-full text-center px-5 py-3 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 transition"
              >
                Call {SITE.phone}
              </a>

              <Link
                to="/contact"
                className="mt-3 inline-block w-full text-center px-5 py-3 rounded-xl border border-slate-300 text-slate-900 font-semibold hover:bg-slate-50 transition"
              >
                Contact Form
              </Link>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <h3 className="text-lg font-extrabold text-slate-900">
                All Services
              </h3>
              <ul className="mt-3 space-y-2 text-sm">
                {SERVICES.map((s) => (
                  <li key={s.slug}>
                    <Link
                      className={`hover:text-blue-600 ${
                        s.slug === service.slug
                          ? "text-slate-900 font-semibold"
                          : "text-slate-600"
                      }`}
                      to={`/services/${s.slug}`}
                    >
                      {s.title}
                    </Link>
                  </li>
                ))}
              </ul>

              <Link
                to="/services"
                className="mt-4 inline-block text-sm font-semibold text-blue-600 hover:text-blue-700"
              >
                ← Back to Services
              </Link>
            </div>
          </div>
        </aside>
      </section>
    </>
  );
}
